import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useListContext, useTranslate } from 'react-admin';
import { MenuItem, Checkbox, ListItemText, FormControl, InputLabel, Button, Box, List, Chip, useTheme, useMediaQuery } from '@mui/material';
import ExpandMoreIcon from '@/assets/icons/arrow-down-icon.svg?react';
import ExpandLessIcon from '@/assets/icons/arrow-up-icon.svg?react';
import { styled } from '@mui/material/styles';
import isEqual from 'lodash/isEqual';
import { Palette } from '@/themes/themeOverrides/Palette';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';

const StyledCustomSelectArrayInput = styled('div')(({ theme }) => ({
    '&.custom-select-array-input': {
        position: 'relative',
        '&.always-on-filter > .MuiFormControl-root': {
            paddingRight: '0',
            '& .chips-container::after': {
                left: '496px',
            },
        },
        '& > .MuiFormControl-root': {
            height: '48px',
            minWidth: '320px',
            maxWidth: '574px',
            cursor: 'pointer',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                minWidth: '100%',
            },
            '&.active': {
                borderColor: Palette.black,
                boxShadow: `0 0 0 2px ${Palette.yellow.yellow200}`,
            },
            '& .MuiFormLabel-root': {
                top: '-2px',
                fontSize: '14px',
                transition: 'all 0.1s ease-out',
                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                    fontSize: '16px',
                },
                '&.top': {
                    left: '-5px',
                    top: '-27px',
                    fontSize: '11px',
                    color: Palette.gray.gray600,
                },
            },
            '& > svg': {
                right: '39px',
                position: 'absolute',
                top: '12px',
                zIndex: '3',
                pointerEvents: 'none',
                display: 'none',
            },
        },
        '& .chips-container': {
            margin: '10px 26px -31px 14px',
            minHeight: '24px',
            pointerEvents: 'auto',
            zIndex: '1',
            display: 'flex',
            flexWrap: 'no-wrap',
            gap: 1,
            mb: 2,
            maxWidth: 'max-content',
            overflow: 'hidden',
            position: 'relative',
            '& .MuiChip-root': {
                padding: '4px 5px 4px 8px',
                '& .MuiChip-deleteIcon': {
                    pointerEvents: 'auto',
                },
                '& .MuiChip-label': {
                    padding: '0 2px 0 0',
                },
            },
            '&::after': {
                content: '""',
                width: '40px',
                height: '100%',
                position: 'absolute',
                left: '450px',
                top: '0px',
                background: `linear-gradient(90deg, rgba(247, 247, 247, 0.00) 0%, ${Palette.gray.gray75} 100%)`,
                zIndex: '2',
                pointerEvents: 'none',
            },
        },
        '& .MuiOutlinedInput-root': {
            '& .MuiSelect-icon': {
                top: '16px',
            },
        },
        '& .checkboxes-filter__popup': {
            background: Palette.white,
            width: 'calc(100% - 8px)',
            boxShadow: Palette.boxShadow.elevation2,
            borderRadius: '12px',
            '&.force-desktop-on-mobile': {
                [theme.breakpoints.down('md')]: {
                    position: 'relative',
                    boxShadow: `${Palette.boxShadow.elevation16} !important`,
                },
            },
            '&__checkboxes': {
                maxHeight: '228px',
                overflowX: 'auto',
                display: 'block',
                padding: '0 !important',
            },
            '&::-webkit-scrollbar-track': {
                background: 'transparent',
            },
            '&.open': {
                display: 'block',
            },
            '& .MuiList-root': {
                padding: '5px 0 0 0',
                '& .MuiMenuItem-root': {
                    padding: '0 12px',
                    '&:hover': {
                        background: Palette.white,
                        '& > .MuiBox-root': {
                            background: Palette.yellow.yellow50,
                            [theme.breakpoints.down('md')]: {
                                background: 'transparent',
                            },
                        },
                    },
                    '& > .MuiBox-root': {
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        [theme.breakpoints.down('md')]: {
                            padding: '10px 12px 9px 12px',
                        },
                    },
                    '& .MuiCheckbox-root': {
                        padding: '0',
                        marginRight: '8px',
                    },
                    '& .MuiListItemText-root': {
                        '& .MuiTypography-root': {
                            fontSize: '14px',
                            wordBreak: 'break-word',
                        },
                    },
                    '&.select-all': {
                        borderBottom: `2px solid ${Palette.gray.gray100}`,
                        margin: 0,
                        width: '100%',
                        marginBottom: '12px',
                        padding: '0 12px 2px 12px !important',
                        '&:hover .MuiBox-root ': {
                            background: 'transparent',
                        },
                        '& .MuiTypography-root': {
                            fontSize: '14px',
                            paddingLeft: '4px',
                        },
                    },
                },
            },
            '& .actions': {
                padding: '20px 24px 24px 24px',
                position: 'sticky',
                bottom: '-1px',
                background: Palette.white,
                borderRadius: '0 0 12px 12px',
                [theme.breakpoints.down('md')]: {
                    position: 'relative',
                },
            },
        },
    },
}));

const CustomSelectArrayInput = ({
    source,
    choices,
    label = '',
    defaultValue = [],
    value = [],
    onChange,
    lockedIds = [],
    alwaysDropdown = false,
    forceDesktopOnMobile = false,
    ...rest
}) => {
    const translate = useTranslate();
    const [open, setOpen] = useState(false);
    const [tempValue, setTempValue] = useState(() => {
        const currentValue = value.length ? value : defaultValue;
        return currentValue;
    });
    const [pendingValue, setPendingValue] = useState(() => {
        const currentValue = value.length ? value : defaultValue;
        return currentValue;
    });
    const { setFilters, filterValues } = useListContext();
    const containerRef = useRef(null);
    const suppressNextSyncRef = useRef(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')) && !alwaysDropdown && !forceDesktopOnMobile;

    useEffect(() => {
        if (onChange) {
            return;
        }

        if (suppressNextSyncRef.current) {
            suppressNextSyncRef.current = false;
            return;
        }

        const currentValue = filterValues?.[source];

        if (currentValue === undefined) {
            return;
        }

        setTempValue(currentValue);
        setPendingValue(currentValue);
    }, [filterValues, onChange, source]);

    useEffect(() => {
        if (!onChange) {
            return;
        }

        if (suppressNextSyncRef.current) {
            suppressNextSyncRef.current = false;
            return;
        }

        const nextValue = Array.isArray(value) ? value : [];
        setTempValue(nextValue);
        setPendingValue(nextValue);
    }, [onChange, value]);

    useEffect(() => {
        const handleClickOutside = event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobile, open]);

    const commitValue = newValue => {
        if (onChange) {
            onChange(newValue);
            return;
        }

        const updatedFilters = { ...filterValues };

        if (newValue.length === 0) {
            delete updatedFilters[source];
        } else {
            updatedFilters[source] = newValue;
        }

        setFilters(updatedFilters, false);
    };

    const handleDelete = id => {
        if (lockedIds.includes(id)) {
            return;
        }

        const newValue = tempValue.filter(val => val !== id);
        suppressNextSyncRef.current = true;
        setTempValue(newValue);
        setPendingValue(newValue);
        commitValue(newValue);
    };

    const toggleSelectAll = () => {
        const allIds = choices.map(choice => choice.id);
        const allSelected = allIds.every(id => pendingValue.includes(id));
        const newValue = allSelected ? allIds.filter(id => lockedIds.includes(id)) : allIds;
        setPendingValue(newValue);
    };

    const toggleSelection = id => {
        if (lockedIds.includes(id)) {
            return;
        }

        setPendingValue(prev => {
            return prev.includes(id) ? prev.filter(val => val !== id) : [...prev, id];
        });
    };

    const handleApply = () => {
        setTempValue(pendingValue);
        commitValue(pendingValue);
        setOpen(false);
    };

    const classNames = ['custom-select-array-input', isMobile && 'mobile', rest.alwaysOn && 'always-on-filter'].filter(Boolean).join(' ');

    return (
        <StyledCustomSelectArrayInput className={classNames} ref={containerRef}>
            <FormControl
                fullWidth
                variant='outlined'
                className={open ? 'active' : ''}
                onClick={() => {
                    setPendingValue(tempValue);
                    setOpen(true);
                }}
            >
                <InputLabel className={tempValue.length > 0 ? 'top' : ''}>{label}</InputLabel>
                <Box
                    className='chips-container'
                    onClick={event => {
                        event.stopPropagation();
                    }}
                >
                    {tempValue.length > 0 &&
                        tempValue.map(id => {
                            const selectedChoice = choices.find(choice => choice.id === id);
                            return (
                                <Chip
                                    key={id}
                                    label={selectedChoice?.name}
                                    onDelete={event => {
                                        event.stopPropagation();
                                        handleDelete(id);
                                    }}
                                    size='small'
                                    sx={{ margin: '2px' }}
                                />
                            );
                        })}
                </Box>
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </FormControl>

            {open && (
                <Box
                    className={`checkboxes-filter__popup ${forceDesktopOnMobile ? 'force-desktop-on-mobile' : ''}`}
                    sx={{ zIndex: 10, position: 'absolute', top: '100%' }}
                >
                    <List>
                        <MenuItem onClick={toggleSelectAll} className='select-all'>
                            <Box>
                                <Checkbox checked={choices.length > 0 && pendingValue.length === choices.length} />
                                <ListItemText primary={translate('common.filter.selectAll')} />
                            </Box>
                        </MenuItem>
                        <List className='checkboxes-filter__popup__checkboxes'>
                            {choices.map(choice => (
                                <MenuItem key={choice.id} value={choice.id} onClick={() => toggleSelection(choice.id)}>
                                    <Box>
                                        <Checkbox checked={pendingValue.includes(choice.id)} disabled={lockedIds.includes(choice.id)} />
                                        <ListItemText primary={choice.name} />
                                    </Box>
                                </MenuItem>
                            ))}
                        </List>
                    </List>

                    <Box className='actions'>
                        <Button variant='contained' color='primary' fullWidth onClick={handleApply} disabled={isEqual(tempValue, pendingValue)}>
                            {translate('common.apply')}
                        </Button>
                    </Box>
                </Box>
            )}
        </StyledCustomSelectArrayInput>
    );
};

CustomSelectArrayInput.propTypes = {
    source: PropTypes.string.isRequired,
    choices: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
        }),
    ).isRequired,
    label: PropTypes.string,
    defaultValue: PropTypes.array,
    value: PropTypes.array,
    onChange: PropTypes.func,
    lockedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    alwaysDropdown: PropTypes.bool,
    forceDesktopOnMobile: PropTypes.bool,
    emptyText: PropTypes.string,
};

export default CustomSelectArrayInput;
